import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { FileText, Check, Clock, XCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientContracts() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContracts();
  }, [token]);

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${API}/contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContracts(response.data);
    } catch (error) {
      console.error('Failed to fetch contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending_signature: { class: 'status-pending', label: 'En attente', icon: Clock },
      signed: { class: 'status-approved', label: 'Signé', icon: Check },
      active: { class: 'status-active', label: 'Actif', icon: Check },
      terminated: { class: 'status-rejected', label: 'Résilié', icon: XCircle }
    };
    const { class: cls, label, icon: Icon } = config[status] || config.pending_signature;
    return (
      <Badge className={cls}>
        <Icon className="w-3 h-3 mr-1" />
        {label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {contracts.length === 0 ? (
          <Card className="card-premium">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucun contrat</p>
              <p className="text-sm text-muted-foreground mt-1">
                Souscrivez à un produit pour créer votre premier contrat
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {contracts.map(contract => (
              <Card key={contract.id} className="card-premium" data-testid={`contract-${contract.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{contract.product?.name || 'Produit'}</h3>
                        <p className="text-sm text-muted-foreground">
                          Souscrit le {new Date(contract.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-xl text-[#D4AF37]">
                        €{contract.amount?.toLocaleString()}
                      </p>
                      {getStatusBadge(contract.status)}
                    </div>
                  </div>
                  
                  {contract.signed_at && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-xs text-muted-foreground">
                        Signé le {new Date(contract.signed_at).toLocaleDateString()} à {new Date(contract.signed_at).toLocaleTimeString()}
                      </p>
                      {contract.document_hash && (
                        <p className="text-xs text-muted-foreground font-mono mt-1">
                          Hash: {contract.document_hash.slice(0, 16)}...
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
